/* Panneau de correction IA réutilisable (devoir ou copie d'examen). Piloté par */
/* callbacks : charge la correction existante, lance l'IA, enregistre l'override, */
/* publie. L'IA propose — le prof édite avant de publier. La copie reste visible */
/* à côté des notes : on ne corrige pas à l'aveugle. */

import { useEffect, useRef, useState } from 'react';
import { Check, PencilSimpleLine } from '@phosphor-icons/react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { numericTotals } from '../lib/grading-scale';
import { LetterSelect, type LetterOption } from './letter-select';

export interface CorrSubPoint {
  label: string;
  points: number;
  acquired: boolean;
}
export interface CorrItem {
  sectionIndex: number;
  sectionTitle: string;
  score: number;
  max: number;
  feedback: string;
  unread?: boolean;
  subPoints?: CorrSubPoint[];
  letter?: string;
  scaleId?: string;
  letterLabel?: string;
  letterOptions?: LetterOption[];
}
export interface CorrResult {
  totalScore: number;
  totalMax: number;
  overallFeedback: string;
  items: CorrItem[];
  model: string;
}
export interface CorrRow {
  result: CorrResult;
  teacher_override: CorrResult | null;
  published_to_student: boolean;
}
export interface CorrImage {
  url: string;
  content_type: string;
}

export interface CorrectionPanelProps {
  load: () => Promise<CorrRow | null>;
  run: () => Promise<CorrRow>;
  saveOverride: (result: CorrResult) => Promise<void>;
  publish: (published: boolean) => Promise<void>;
  onChange?: () => void;
  loadImages?: () => Promise<CorrImage[]>;
}

/* Une section non lue ou notée par lettres sort du total numérique. */
const totals = numericTotals;

/* D1.5 — publier une note amputée la gonflerait : tant qu'une section est non
   lue, la publication reste bloquée et le prof corrige la section à la main. */
function unreadLabel(count: number): string {
  const s = count > 1 ? 's' : '';
  return `${count} section${s} non lue${s} à corriger`;
}

/* Résumé par copie à l'ouverture du panneau (NAI-218, 2/2 point 1). */
function unreadSummary(count: number, total: number): string {
  const s = count > 1 ? 's' : '';
  const verb = count > 1 ? 'nécessitent' : 'nécessite';
  return `${count} section${s} sur ${total} ${verb} une correction manuelle`;
}

export default function CorrectionPanel({ load, run, saveOverride, publish, onChange, loadImages }: CorrectionPanelProps) {
  const [draft, setDraft] = useState<CorrResult | null>(null);
  const [published, setPublished] = useState(false);
  const [hasOverride, setHasOverride] = useState(false);
  const [images, setImages] = useState<CorrImage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmingRecorrect, setConfirmingRecorrect] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevUnreadCountRef = useRef(0);

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => setSuccess(null), 3000);
  };

  const apply = (row: CorrRow | null) => {
    const eff = row?.teacher_override ?? row?.result ?? null;
    setDraft(eff ? { ...eff, items: eff.items.map((i) => ({ ...i })) } : null);
    setPublished(row?.published_to_student ?? false);
    setHasOverride(Boolean(row?.teacher_override));
    prevUnreadCountRef.current = eff ? eff.items.filter((i) => i.unread).length : 0;
  };

  useEffect(() => {
    load()
      .then(apply)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (!loadImages) return;
    loadImages()
      .then(setImages)
      .catch(() => setImages([]));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => () => { if (successTimerRef.current) clearTimeout(successTimerRef.current); }, []);

  const runCorrection = async () => {
    setBusy(true);
    setError(null);
    try {
      apply(await run());
      setConfirmingRecorrect(false);
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const requestRecorrect = () => {
    if (hasOverride) {
      setConfirmingRecorrect(true);
      return;
    }
    void runCorrection();
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await saveOverride({ ...draft, ...totals(draft.items) });
      const resolvedUnread = prevUnreadCountRef.current - unreadCount;
      showSuccess(resolvedUnread > 0 ? 'Section corrigée — correction enregistrée.' : 'Correction enregistrée.');
      prevUnreadCountRef.current = unreadCount;
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const setItem = (idx: number, patch: Partial<CorrItem>) =>
    setDraft((d) => (d ? { ...d, items: d.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) } : d));

  const draftTotals = draft ? totals(draft.items) : null;
  const unreadCount = draft ? draft.items.filter((it) => it.unread).length : 0;
  const publishBlocked = !published && unreadCount > 0;

  const togglePublish = async () => {
    if (publishBlocked) {
      setError(`${unreadLabel(unreadCount)} — publication refusée.`);
      return;
    }
    setBusy(true);
    try {
      await publish(!published);
      setPublished((p) => !p);
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {error && <Alert>{error}</Alert>}
      {success && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-card bg-[var(--naia-green-bg)] border border-[var(--naia-green-deep)]/20 text-[var(--naia-green-deep)] text-[13px] font-semibold">
          <Check size={15} weight="bold" />
          {success}
        </div>
      )}

      <div className="flex flex-col gap-[14px] sm:flex-row sm:items-start">
        {images.length > 0 && (
          <div className="sm:w-[38%] sm:flex-none flex flex-col gap-2">
            {images.map((img, i) => (
              <a key={`${img.url}-${i}`} href={img.url} target="_blank" rel="noreferrer">
                <img
                  src={img.url}
                  alt={`Copie de l'élève — photo ${i + 1}`}
                  className="w-full max-h-[440px] object-contain rounded-card border border-border bg-card"
                />
              </a>
            ))}
          </div>
        )}

        <div className="flex-1 min-w-0 flex flex-col gap-[10px]">
          {!draft ? (
            <Button disabled={busy} onClick={() => void runCorrection()}>
              {busy ? 'Correction en cours…' : '✨ Corriger avec Naïa'}
            </Button>
          ) : (
            <>
              {unreadCount > 0 && (
                <div
                  data-testid="unread-summary"
                  className="rounded-card px-3 py-2 text-[13px] font-semibold"
                  style={{ background: 'var(--naia-warn-bg)', color: 'var(--naia-tertiary)' }}
                >
                  {unreadSummary(unreadCount, draft.items.length)}
                </div>
              )}
              <div style={{ fontWeight: 800, color: 'var(--naia-text-primary)' }}>
                {draftTotals!.totalMax === 0 && unreadCount > 0 ? (
                  'Correction manuelle nécessaire'
                ) : unreadCount > 0 ? (
                  <>
                    Total : {draftTotals!.totalScore} / {draftTotals!.totalMax}{' '}
                    <span className="text-[13px] font-semibold text-muted-foreground">
                      (provisoire — {unreadCount} section{unreadCount > 1 ? 's' : ''} à corriger)
                    </span>
                  </>
                ) : (
                  <>Total : {draftTotals!.totalScore} / {draftTotals!.totalMax}</>
                )}
              </div>
              {draft.items.map((it, i) => (
                <div
                  key={i}
                  data-testid={it.unread ? 'section-unread' : undefined}
                  style={
                    it.unread
                      ? {
                          border: '1.5px solid var(--naia-tertiary)',
                          background: 'var(--naia-warn-bg)',
                          borderRadius: 8,
                          padding: 10,
                        }
                      : { border: '1px solid var(--naia-line)', borderRadius: 8, padding: 10 }
                  }
                >
                  <div style={{ fontWeight: 700, color: 'var(--naia-text-primary)' }}>{it.sectionTitle || `Section ${i + 1}`}</div>
                  {it.unread ? (
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <Badge variant="secondary">Non lue</Badge>
                      <span className="text-[12px] text-muted-foreground">Section illisible — à corriger manuellement.</span>
                      <Button disabled={busy} onClick={() => setItem(i, { unread: false })}>
                        <PencilSimpleLine size={15} weight="bold" />
                        Corriger manuellement
                      </Button>
                    </div>
                  ) : (
                    it.scaleId !== undefined ? (
                      <label style={{ fontSize: 13, color: 'var(--naia-text-secondary)' }}>
                        Lettre :{' '}
                        <LetterSelect letter={it.letter} options={it.letterOptions ?? []} onPick={(patch) => setItem(i, patch)} />
                      </label>
                    ) : (
                    <label style={{ fontSize: 13, color: 'var(--naia-text-secondary)' }}>
                      Note :{' '}
                      <Input
                        type="number"
                        value={it.score}
                        min={0}
                        max={it.max}
                        step={0.5}
                        onChange={(e) => setItem(i, { score: Number(e.target.value) })}
                        style={{ width: 64 }}
                      />{' '}
                      / {it.max}
                    </label>
                    )
                  )}
                  {!it.unread && it.subPoints && it.subPoints.length > 0 && (
                    <ul className="mt-1.5 flex flex-col gap-0.5 list-none p-0 m-0">
                      {it.subPoints.map((sp, spIdx) => (
                        <li key={spIdx} className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                          <Badge variant={sp.acquired ? 'default' : 'secondary'}>
                            {sp.acquired ? 'Acquis' : 'Non acquis'}
                          </Badge>
                          <span>{sp.label} ({sp.points} pt{sp.points > 1 ? 's' : ''})</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <textarea
                    value={it.feedback}
                    onChange={(e) => setItem(i, { feedback: e.target.value })}
                    rows={2}
                    style={{ width: '100%', marginTop: 6, fontFamily: 'inherit', fontSize: 13, borderRadius: 6, border: '1px solid var(--naia-line)', padding: 6 }}
                  />
                </div>
              ))}
              <label style={{ fontSize: 13, color: 'var(--naia-text-secondary)' }}>
                Appréciation générale
                <textarea
                  value={draft.overallFeedback}
                  onChange={(e) => setDraft((d) => (d ? { ...d, overallFeedback: e.target.value } : d))}
                  rows={2}
                  style={{ width: '100%', marginTop: 4, fontFamily: 'inherit', fontSize: 13, borderRadius: 6, border: '1px solid var(--naia-line)', padding: 6 }}
                />
              </label>

              {confirmingRecorrect ? (
                <div className="flex flex-col gap-2 rounded-card border border-border p-3">
                  <p className="m-0 text-[13px] text-foreground">
                    Une nouvelle correction par l'IA remplacera votre correction enregistrée et dépubliera la copie.
                  </p>
                  <div className="flex gap-[10px] flex-wrap">
                    <Button disabled={busy} onClick={() => void runCorrection()}>
                      {busy ? 'Correction en cours…' : 'Confirmer la re-correction'}
                    </Button>
                    <Button variant="secondary" disabled={busy} onClick={() => setConfirmingRecorrect(false)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-[10px] flex-wrap justify-start">
                  <Button variant="secondary" disabled={busy} onClick={requestRecorrect}>Re-corriger</Button>
                  <Button disabled={busy} onClick={() => void save()}>Enregistrer</Button>
                  <Button
                    variant={published ? 'default' : 'secondary'}
                    disabled={busy || publishBlocked}
                    onClick={() => void togglePublish()}
                  >
                    {published ? "✓ Publié à l'élève" : "Publier à l'élève"}
                  </Button>
                </div>
              )}
              {publishBlocked && (
                <p className="text-[12px] text-muted-foreground">
                  {unreadLabel(unreadCount)} — corrigez la section à la main avant de publier.
                </p>
              )}
              <p className="text-muted-foreground" style={{ fontSize: 12 }}>Proposition IA — à relire et valider avant publication.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
